import { useEffect, useMemo, useState } from "react";
import { useSendManager } from "../context/SendManagerContext";

export default function SendBanner() {
  const { job, batch, stopJob, cancelServerBatch } = useSendManager();
  const [hideServer, setHideServer] = useState(false);
  const [hideClient, setHideClient] = useState(false);
  const clientVisible = job && (job.status === 'scheduled' || job.status === 'sending');
  const serverVisible = !!(batch && ['running','queued','cancelled','done','error'].includes(batch.status)) && !hideServer;
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    if (!clientVisible && !serverVisible) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [clientVisible, serverVisible]);

  const clientText = useMemo(() => {
    if (!job) return null;
    if (job.status === 'scheduled') {
      const eta = job.scheduledAt ? new Date(job.scheduledAt).toLocaleString() : '';
      return `Programado para ${eta} · Destinatarios: ${job.total}`;
    }
    if (job.status === 'sending') {
      let countdown = '';
      if (job.nextSendAt && job.current < job.total) {
        const diff = Math.max(0, Math.floor((job.nextSendAt - nowMs) / 1000));
        countdown = ` · Próximo en ${diff}s`;
      }
      const rangeInfo = (job.minDelaySec && job.maxDelaySec) ? ` (intervalo aleatorio ${job.minDelaySec}-${job.maxDelaySec}s)` : '';
      return `Destinatarios: ${job.total} · Progreso: ${job.current}/${job.total} · Éxitos: ${job.sent} · Fallos: ${job.failed}${countdown}${rangeInfo}`;
    }
    return null;
  }, [job, nowMs]);

  if (serverVisible) {
    const total = (batch.items?.length) || batch.total || (batch.sent||0)+(batch.failed||0) || 0;
    const sent = batch.sent || 0;
    const failed = batch.failed || 0;
    const remaining = total ? (total - sent - failed) : 0;
    const pct = total ? Math.min(100, Math.round(((sent+failed)/total)*100)) : 0;
    const nextTs = batch.nextPlannedAt ? new Date(batch.nextPlannedAt).getTime() : null;
    let countdown = '';
    if (nextTs) {
      const diff = Math.max(0, Math.floor((nextTs - nowMs) / 1000));
      countdown = diff + 's';
    }
    return (
      <div style={{ position:'fixed', bottom:20, right:20, zIndex:5000, background:'#0b1220', color:'#e2e8f0', border:'1px solid #334155', borderRadius:12, padding:'0.9rem 1rem 0.75rem', boxShadow:'0 8px 24px rgba(0,0,0,0.25)', display:'flex', flexDirection:'column', gap:6, maxWidth:460 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <strong style={{ fontSize:14 }}>Envío desatendido {pct}%</strong>
            <div style={{ fontSize:11, color:'#94a3b8', letterSpacing:0.2 }}>Este panel no detiene el envío si lo cierras.</div>
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            {batch.status === 'running' && (
              <button onClick={cancelServerBatch} style={{ background:'#ef4444', color:'#fff', border:'none', borderRadius:6, padding:'0.35rem 0.6rem', fontSize:12, cursor:'pointer' }}>Cancelar</button>
            )}
            <button
              onClick={() => {
                // Ocultar el banner hasta que cambie el batch (por simplicidad se remueve batch del estado visual)
                // No cancelamos el proceso; solo lo ocultamos moviendo estado local: retornamos null en render forzando false serverVisible
                // Solución simple: añadir un flag en state local
                setHideServer(true);
              }}
              title="Ocultar panel"
              aria-label="Cerrar"
              style={{ background:'transparent', border:'none', color:'#94a3b8', fontSize:16, cursor:'pointer', lineHeight:1 }}
            >✕</button>
          </div>
        </div>
        <div style={{ fontSize:13, display:'flex', flexWrap:'wrap', gap:10 }}>
          <span>Enviados: {sent}</span>
          <span>Fallidos: {failed}</span>
          <span>Restantes: {remaining}</span>
          <span>Estado: {batch.status}</span>
          <span>Intervalo 7-20s</span>
          {countdown && batch.status==='running' && <span>Próximo: {countdown}</span>}
        </div>
        {!!(batch.errors?.length) && (
          <details style={{ fontSize:12 }}>
            <summary>Errores ({batch.errors.length})</summary>
            <ul style={{ margin:0, paddingLeft:16 }}>
              {batch.errors.slice(-6).map((e,i)=>(<li key={i}>{(e.number||e.index)||'#'}: {e.error}</li>))}
            </ul>
          </details>
        )}
        {batch.status==='done' && <div style={{ color:'#22c55e', fontSize:13 }}>Proceso finalizado.</div>}
        {batch.status==='cancelled' && <div style={{ color:'#f59e0b', fontSize:13 }}>Proceso cancelado.</div>}
      </div>
    );
  }

  if (!clientVisible || hideClient) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 5000,
        background: '#0b1220',
        color: '#e2e8f0',
        border: '1px solid #334155',
        borderRadius: 12,
        padding: '0.75rem 1rem',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        maxWidth: 420,
      }}
    >
      <div style={{ fontSize: 14, lineHeight: 1.2 }}>{clientText}</div>
      {job.status === 'sending' ? (
        <button
          onClick={stopJob}
          style={{
            marginLeft: 6,
            background: '#ef4444',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '0.35rem 0.6rem',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Parar envío
        </button>
      ) : null}
      <button
        onClick={() => setHideClient(true)}
        title="Ocultar panel"
        aria-label="Cerrar"
        style={{ background:'transparent', border:'none', color:'#94a3b8', fontSize:16, cursor:'pointer', lineHeight:1 }}
      >✕</button>
    </div>
  );
}
